/**
 * The deck's notes and mind maps, read and written as a list.
 *
 * `SyncDeckMeta.materials` is the stored index (see `DeckMaterialsIndex`);
 * this module is the only thing that reads and edits it, so the rules below
 * are stated once for both clients.
 *
 * THE IMPLICIT FIRST ITEM. Every deck predating this had exactly one note and
 * one map, neither of them written down anywhere — the note at
 * `DEFAULT_NOTE_ID`, the map at the record keyed by the deck itself. So an
 * ABSENT list means "the implicit one", and an EMPTY list means "none, and
 * somebody said so". The distinction is what lets a deck delete its last note
 * without it reappearing, and what lets every existing deck keep its note
 * without a migration: the first write materialises the implicit entry and
 * from then on the list is the whole truth.
 *
 * Naming and deletion follow `nextMaterialName` — auto-numbered at creation,
 * renameable after, and deleting leaves the gap.
 *
 * TWO VIEWS OF THE LIST. The index merges PER ENTRY across devices
 * (`sync-engine/framework/deck-materials-merge.ts`): a removal is a
 * tombstone (`deletedAtISO`) that must travel with the list, or a stale
 * copy from another device would list the item again, and every edit of an
 * entry stamps its own `updatedAtISO` so the newer edit wins per entry. So
 * READERS take `deckNoteItems` / `deckMindmapItems` (tombstones hidden) and
 * WRITERS map over `storedDeckNotes` / `storedDeckMindmaps` (tombstones
 * carried forward). A writer that read the filtered list would drop every
 * tombstone on its next write — and resurrect the notes they stood for.
 */

import type {
  DeckMaterialsIndex,
  DeckMindmapItem,
  DeckNoteItem,
} from '../../sync-engine/schema/shared-types';
import { nextMaterialName } from './naming';
import { DEFAULT_MINDMAP_ID } from './types';

/** The one note every pre-index deck has. Mirrors `notes/note-identity`,
 *  which this module must not import (it owns the IndexedDB naming and
 *  pulls a wider surface than the pure materials layer wants). */
const DEFAULT_NOTE_ID = 'default';

/** An entry the user has not removed. */
export function isLiveMaterial(item: { deletedAtISO?: string }): boolean {
  return !item.deletedAtISO;
}

/**
 * Every STORED note entry, tombstones included, with the implicit first one
 * materialised. For writers only — see the module doc.
 */
export function storedDeckNotes(index: DeckMaterialsIndex | undefined): DeckNoteItem[] {
  if (index?.notes) return [...index.notes];
  return [{ noteId: DEFAULT_NOTE_ID, addedAtISO: '' }];
}

/** Every STORED mind-map entry, tombstones included. For writers only. */
export function storedDeckMindmaps(
  index: DeckMaterialsIndex | undefined,
): DeckMindmapItem[] {
  if (index?.mindmaps) return [...index.mindmaps];
  return [{ mindmapId: DEFAULT_MINDMAP_ID, addedAtISO: '' }];
}

/** The deck's notes as the user sees them: the implicit first one
 *  materialised, removed ones hidden. */
export function deckNoteItems(index: DeckMaterialsIndex | undefined): DeckNoteItem[] {
  return storedDeckNotes(index).filter(isLiveMaterial);
}

/** The deck's mind maps as the user sees them. See {@link deckNoteItems}. */
export function deckMindmapItems(
  index: DeckMaterialsIndex | undefined,
): DeckMindmapItem[] {
  return storedDeckMindmaps(index).filter(isLiveMaterial);
}

export interface NewMaterialInput {
  /** A fresh id. Generated by the caller — this module stays pure. */
  id: string;
  /** Pre-translated "Note" / "Mind map". */
  namePrefix: string;
  nowISO: string;
  /** The source it is being generated from, when there is one. */
  fromSourceId?: string;
}

/** Add a note, named one past the highest number already in the deck. */
export function addDeckNote(
  index: DeckMaterialsIndex | undefined,
  input: NewMaterialInput,
): DeckMaterialsIndex {
  // The number is measured against the LIVE notes (a removed one leaves its
  // gap, a renamed one frees its number); the list written is the STORED one.
  const title = nextDeckNoteName(index, input.namePrefix);
  return {
    ...index,
    notes: [
      ...storedDeckNotes(index),
      {
        noteId: input.id,
        title,
        addedAtISO: input.nowISO,
        ...(input.fromSourceId ? { fromSourceId: input.fromSourceId } : {}),
      },
    ],
  };
}

/** Add a mind map, named one past the highest number already in the deck. */
export function addDeckMindmap(
  index: DeckMaterialsIndex | undefined,
  input: NewMaterialInput,
): DeckMaterialsIndex {
  const title = nextMaterialName(
    titlesOf(deckMindmapItems(index), input.namePrefix),
    input.namePrefix,
  );
  return {
    ...index,
    mindmaps: [
      ...storedDeckMindmaps(index),
      {
        mindmapId: input.id,
        title,
        addedAtISO: input.nowISO,
        ...(input.fromSourceId ? { fromSourceId: input.fromSourceId } : {}),
      },
    ],
  };
}

/** The name the next auto-numbered note of the deck takes — see {@link addDeckNote}. */
export function nextDeckNoteName(
  index: DeckMaterialsIndex | undefined,
  namePrefix: string,
): string {
  return nextMaterialName(titlesOf(deckNoteItems(index), namePrefix), namePrefix);
}

/**
 * The titles the next number is measured against. An item with no stored
 * title is the implicit first one, which DISPLAYS as `<prefix> 1` — so it has
 * to count as 1 here, or the second note would also be called "Note 1".
 */
function titlesOf(
  items: readonly { title?: string }[],
  namePrefix: string,
): (string | undefined)[] {
  return items.map(
    (item, position) => item.title ?? (position === 0 ? `${namePrefix} 1` : undefined),
  );
}

/**
 * Remove a note: a TOMBSTONE on its entry, not a shorter list. The entry
 * stays in the stored list with `deletedAtISO` so a device that still lists
 * the note cannot merge it back to life; readers hide it.
 */
export function removeDeckNote(
  index: DeckMaterialsIndex | undefined,
  noteId: string,
  nowISO: string,
): DeckMaterialsIndex {
  return {
    ...index,
    notes: storedDeckNotes(index).map((n) =>
      n.noteId === noteId && isLiveMaterial(n)
        ? { ...n, deletedAtISO: nowISO, updatedAtISO: nowISO }
        : n,
    ),
  };
}

/** See {@link removeDeckNote}. */
export function removeDeckMindmap(
  index: DeckMaterialsIndex | undefined,
  mindmapId: string,
  nowISO: string,
): DeckMaterialsIndex {
  return {
    ...index,
    mindmaps: storedDeckMindmaps(index).map((m) =>
      m.mindmapId === mindmapId && isLiveMaterial(m)
        ? { ...m, deletedAtISO: nowISO, updatedAtISO: nowISO }
        : m,
    ),
  };
}

export function renameDeckNote(
  index: DeckMaterialsIndex | undefined,
  noteId: string,
  title: string,
  nowISO: string,
): DeckMaterialsIndex {
  return {
    ...index,
    notes: storedDeckNotes(index).map((n) =>
      n.noteId === noteId ? { ...n, title, updatedAtISO: nowISO } : n,
    ),
  };
}

export function renameDeckMindmap(
  index: DeckMaterialsIndex | undefined,
  mindmapId: string,
  title: string,
  nowISO: string,
): DeckMaterialsIndex {
  return {
    ...index,
    mindmaps: storedDeckMindmaps(index).map((m) =>
      m.mindmapId === mindmapId ? { ...m, title, updatedAtISO: nowISO } : m,
    ),
  };
}

/**
 * Forget that anything was generated from a source that is leaving the deck.
 *
 * A note or a map is NOT deleted with its source — it is a document the user
 * writes in, or a standalone artefact, and only ever RECORDED which source it
 * came from. That record simply stops being true, so it goes; the material
 * stays exactly where it is.
 */
export function clearMaterialProvenance(
  index: DeckMaterialsIndex | undefined,
  sourceId: string,
  nowISO: string,
): DeckMaterialsIndex {
  const strip = <T extends { fromSourceId?: string; updatedAtISO?: string }>(
    item: T,
  ): T => (item.fromSourceId === sourceId ? omitFromSource(item, nowISO) : item);
  return {
    ...index,
    notes: storedDeckNotes(index).map(strip),
    mindmaps: storedDeckMindmaps(index).map(strip),
  };
}

function omitFromSource<T extends { fromSourceId?: string; updatedAtISO?: string }>(
  item: T,
  nowISO: string,
): T {
  const { fromSourceId: _dropped, ...rest } = item;
  return { ...rest, updatedAtISO: nowISO } as T;
}

/**
 * Undo of {@link clearMaterialProvenance}: put `fromSourceId` back on the
 * items that had it, WITHOUT reverting anything else.
 *
 * Takes the CURRENT index and re-applies one field per item, rather than
 * restoring the pre-change index wholesale — a note or a map created between
 * the removal and the undo would otherwise vanish, which is a worse outcome
 * than the provenance line it was trying to fix.
 */
export function restoreMaterialProvenance(
  current: DeckMaterialsIndex | undefined,
  previous: DeckMaterialsIndex | undefined,
  sourceId: string,
  nowISO: string,
): DeckMaterialsIndex {
  const hadIt = new Set<string>();
  for (const note of previous?.notes ?? [])
    if (note.fromSourceId === sourceId) hadIt.add(`note:${note.noteId}`);
  for (const map of previous?.mindmaps ?? [])
    if (map.fromSourceId === sourceId) hadIt.add(`mindmap:${map.mindmapId}`);
  if (hadIt.size === 0) return { ...current };

  return {
    ...current,
    notes: storedDeckNotes(current).map((note) =>
      hadIt.has(`note:${note.noteId}`)
        ? { ...note, fromSourceId: sourceId, updatedAtISO: nowISO }
        : note,
    ),
    mindmaps: storedDeckMindmaps(current).map((map) =>
      hadIt.has(`mindmap:${map.mindmapId}`)
        ? { ...map, fromSourceId: sourceId, updatedAtISO: nowISO }
        : map,
    ),
  };
}

/**
 * Where a mind map's content lives in the `deckMindmaps` record.
 *
 * The record is keyed by a single string, and every existing map is stored
 * under the bare deck key. So the deck's FIRST map keeps that key verbatim —
 * every map anyone has ever made still opens, with no migration and no schema
 * change — and later maps hang off a suffix. The entity never learns about
 * this; it is a key convention, which is exactly why it costs nothing.
 *
 * The delimiter is `\x01` because a deck key is a USER-AUTHORED PATH and the
 * repo's safe-name gate forbids exactly two bytes in one, `\x00` and `\x01`.
 * Any printable separator can appear in a real deck name, and then two
 * different decks can mint the same key: a deck called "Bio#1" and the map
 * "1" of a deck called "Bio" would both be `Bio#1`, and one would silently
 * open the other's map.
 */
export const MINDMAP_KEY_DELIMITER = '\x01';

export function mindmapRecordKey(deckKey: string, mindmapId: string): string {
  return mindmapId === DEFAULT_MINDMAP_ID
    ? deckKey
    : `${deckKey}${MINDMAP_KEY_DELIMITER}${mindmapId}`;
}

/** The mindmapId a record key carries for `deckKey`, or null if it is not
 *  that deck's. Inverse of {@link mindmapRecordKey}. */
export function mindmapIdFromRecordKey(
  recordKey: string,
  deckKey: string,
): string | null {
  if (recordKey === deckKey) return DEFAULT_MINDMAP_ID;
  const prefix = `${deckKey}${MINDMAP_KEY_DELIMITER}`;
  return recordKey.startsWith(prefix) ? recordKey.slice(prefix.length) : null;
}
